import { createElement } from 'react';
import PropTypes from 'prop-types';
import { BLOCKS } from '@contentful/rich-text-types';

import Container from '../../../Core/Container';
import RichText from '../../../Core/RichText';
import Feature from '../../Cards/Card';
import Product from '../../Cards/Product';
import Blog from '../../Cards/Blog';
import BlogCard from '../../Cards/BlogCard';
import FAQCategoryCard from '../../Cards/FAQCategoryCard';
import ProductStep from '../../Cards/ProductStep';
import PointsTile from '../../Cards/PointsTile';
import FeatureCards from '../../Cards/FeatureCards';
import ProductCards from '../../Cards/ProductCard';
import ProductImageCard from '../../Cards/ProductImageCard';
import BannerIconCard from '../../Cards/BannerIconCard';

import {
  Content,
  Wrapper,
  ShowMore,
  Body,
  BottomBody,
  StyledCtas,
} from './styles';

const columnReturn = string => string.split('-')[0];

const componentResolver = type => {
  switch (type) {
    case 'feature-card':
      return Feature;
    case 'product-card':
      return Product;
    case 'blog-card':
      return Blog;
    case 'blog-card-updated':
      return BlogCard;
    case 'faq-card':
      return FAQCategoryCard;
    case 'product-step-card':
      return ProductStep;
    case 'points-tile':
      return PointsTile;
    case 'feature-cards':
      return FeatureCards;
    case 'product':
      return ProductCards;
    case 'product-image':
      return ProductImageCard;
    case 'banner-icon-card':
      return BannerIconCard;
    default:
      return false;
  }
};

const header = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [BLOCKS.HEADING_2]: (_, children) => <h2>{children}</h2>,
    [BLOCKS.HEADING_3]: (_, children) => <h3>{children}</h3>,
  },
};

const body = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [BLOCKS.OL_LIST]: (_, children) => <ol>{children}</ol>,
    [BLOCKS.LIST_ITEM]: (_, children) => <li>{children}</li>,
  },
};

const MultiColumnLayout = ({
  data: {
    heading = null,
    anchorName,
    headingAlignment,
    subheading = null,
    contentBody,
    bodyCopy,
    ctas = null,
    columnCount = '3',
    cards = null,
    blogPosts,
    bottomContentBody,
    elements,
    sectionContainerHeading,
    contentAlignment,
    backgroundColor,
    backgroundImage,
  },
  componentType,
  className = '',
  containerClass = null,
  contentClass = '',
}) => {
  if (!cards && !componentType) return false;

  const columns = columnCount ? columnReturn(columnCount) : 3;
  const type = componentType.split(' ').join('-').toLowerCase();
  const component = componentResolver(type);
  const content = cards || blogPosts || elements;

  return (
    <Container
      as="section"
      id={anchorName}
      className={className || componentType}
      type={containerClass}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
    >
      {(heading || subheading || sectionContainerHeading) && (
        <Content
          className={`${headingAlignment} ${contentClass} ${contentAlignment}`}
        >
          {(heading || sectionContainerHeading) && (
            <div className="title">
              {heading && <RichText document={heading} options={header} />}
              {sectionContainerHeading && (
                <RichText document={sectionContainerHeading} options={header} />
              )}
            </div>
          )}
          {subheading && (
            <div>
              <RichText document={subheading} options={body} />
            </div>
          )}
        </Content>
      )}
      {(contentBody || bodyCopy) && (
        <Body className={`${headingAlignment} ${contentAlignment}`}>
          {contentBody && <RichText document={contentBody} options={body} />}
          {bodyCopy && <RichText document={bodyCopy} options={body} />}
        </Body>
      )}
      {content && (
        <Wrapper
          className={`
          ${type} 
          ${columnCount && `columns-${columnCount}`}
          ${anchorName} 
          no-bullets
        `}
          columnCount={columns}
          role="list"
        >
          {content.map((data, index) =>
            createElement(component, {
              data,
              index,
              key: data.id,
              className: `columns-${columnCount}`,
            }),
          )}
        </Wrapper>
      )}
      {(ctas || bottomContentBody) && (
        <>
          {bottomContentBody && (
            <BottomBody>
              <RichText document={bottomContentBody} />
            </BottomBody>
          )}
          <ShowMore>
            {ctas && <StyledCtas ctas={ctas} direction="row" />}
          </ShowMore>
        </>
      )}
    </Container>
  );
};

MultiColumnLayout.propTypes = {
  data: PropTypes.shape().isRequired,
  componentType: PropTypes.string.isRequired,
  className: PropTypes.string,
  containerClass: PropTypes.string,
  heading: PropTypes.string,
  subheading: PropTypes.string,
  ctas: PropTypes.arrayOf(PropTypes.shape()),
  columnCount: PropTypes.number,
  cards: PropTypes.arrayOf(PropTypes.shape()),
  contentClass: PropTypes.string,
};

export default MultiColumnLayout;
